import { useEffect, useRef, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { localeNames } from 'shared-locales';
import { Button, ConfirmButton } from 'components/common';
import { Box } from 'components/common/box';
import {
  Checkbox,
  Emails,
  Form,
  FormGroup,
  FormSection,
  NumberStepper,
  ReminderIntervalsSelector,
  Select,
  WorkloadSettings
} from 'components/common/form';
import { UnsavedFormChanges } from 'components/common/notifications';

import type { FormApi } from 'informed';

const AchievementSettingsCompletion = () => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { achievement },
    serverConfig
  } = useReduxState();

  const formApi = useRef<FormApi>(undefined);

  const [loading, setLoading] = useState(false);

  const isExternal = achievement.type === 'external';

  const requireExpiration =
    isExternal &&
    serverConfig.achievementCompletionAttributeConditions.some((condition) => {
      return (
        condition.requireExpiration &&
        achievement.customAttributes?.[condition.key] === condition.value
      );
    });

  const requireWorkload =
    isExternal &&
    serverConfig.achievementCompletionAttributeConditions.some((condition) => {
      return (
        condition.requireWorkload &&
        achievement.customAttributes?.[condition.key] === condition.value
      );
    });

  useEffect(() => {
    if (isExternal && formApi.current) {
      if (requireExpiration) {
        formApi.current.setValue('automaticExpiration', true);
      }
      if (requireWorkload) {
        formApi.current.setValue('workloadToggle', true);
      }
    }
  }, []);

  const handleConfirm = () => {
    formApi.current.submitForm();
    return true;
  };

  const handleSubmit = async (values: typeof initialValues) => {
    try {
      const data = { ...values };

      if (isExternal) {
        if (!data.automaticExpiration) data.monthsBeforeExpiration = null;
        if (!data.workloadToggle) data.workload = null;
      }

      setLoading(true);

      const response = await api.put(
        `/admin/achievements/${achievement.id}`,
        data
      );
      if (!response.data || !response.data.success) throw new Error('error');

      const achievementsResponse = await api.get(
        `/admin/achievements?id=${achievement.id}`
      );

      setLoading(false);
      dispatch.updatePageState({
        achievement: achievementsResponse.data.achievements[0],
        hasUnsavedChanges: false
      });
      dispatch.addNotice({
        text: messages.settingsSavedSuccessfully,
        type: 'success'
      });
    } catch (error) {
      await dispatch.handleErrorDispatch(error);
    }
  };

  const initialValues = {
    automaticExpiration: achievement.monthsBeforeExpiration ? true : false,
    certificateAllowAdding: achievement.certificateAllowAdding,
    certificateAllowDownloading: achievement.certificateAllowDownloading,
    certificateRequiredFromUser: achievement.certificateRequiredFromUser,
    completionExpirationReminderIntervals:
      achievement.completionExpirationReminderIntervals,
    completionExpirationReminders: achievement.completionExpirationReminders,
    completionExpirationRemindersEmailList:
      achievement.completionExpirationRemindersEmailList,
    completionExpirationRemindersOthers:
      achievement.completionExpirationRemindersOthers,
    defaultRemindersLanguage: achievement.defaultRemindersLanguage,
    monthsBeforeExpiration: achievement.monthsBeforeExpiration || 12,
    workload: achievement.workload || 1,
    workloadToggle: achievement.workload ? true : false
  };

  return (
    <Box padding={0} title={messages.achievementCompletionDetails}>
      <Form
        allowEmptyStrings={true}
        getApi={(form) => (formApi.current = form)}
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {({ formState }) => (
          <>
            {isExternal && (
              <FormSection maxWidth="100%">
                <UnsavedFormChanges
                  initialValues={initialValues}
                  styles={{ marginBottom: 25 }}
                />
              </FormSection>
            )}

            {isExternal && (
              <FormSection maxWidth="100%">
                <WorkloadSettings
                  disabled={requireWorkload}
                  fieldWorkloadEnabled="workloadToggle"
                  fieldWorkloadValue="workload"
                  workloadEnabledProps={{ label: messages.achievementWorkload }}
                  workloadValueProps={{
                    label: messages.achievementWorkloadTime
                  }}
                />
              </FormSection>
            )}

            <FormSection maxWidth="100%">
              {!isExternal && (
                <UnsavedFormChanges
                  initialValues={initialValues}
                  styles={{ marginBottom: 25 }}
                />
              )}
              <FormGroup label={messages.commonAttachments}>
                <Checkbox
                  field="certificateAllowDownloading"
                  id="certificateAllowDownloading"
                  label={messages.achievementAllowUserDownloadingAttachment}
                />

                <Checkbox
                  field="certificateAllowAdding"
                  id="certificateAllowAdding"
                  label={messages.achievementAllowUserAddingAttachment}
                  onValueChange={(value: boolean) => {
                    if (value === false) {
                      formApi.current.setValue(
                        'certificateRequiredFromUser',
                        false
                      );
                    }
                  }}
                  style={{ marginTop: 5 }}
                />

                <Checkbox
                  disabled={!formState.values.certificateAllowAdding}
                  field="certificateRequiredFromUser"
                  id="certificateRequiredFromUser"
                  label={messages.achievementRequireAttachmentFromUser}
                  style={{ marginTop: 5 }}
                />
              </FormGroup>
            </FormSection>

            {isExternal && (
              <FormSection maxWidth="100%">
                <FormGroup label={messages.settingsCompletionExpiration}>
                  <Checkbox
                    disabled={requireExpiration}
                    field="automaticExpiration"
                    id="automaticExpiration"
                    label={messages.achievementCompletionAutomaticExpiration}
                  />
                  <NumberStepper
                    disabled={!formState.values.automaticExpiration}
                    field="monthsBeforeExpiration"
                    id="monthsBeforeExpiration"
                    label={messages.settingsCompletionExpirationTime}
                    max={1200}
                    min={1}
                    stepValue={1}
                    style={{ marginTop: 10 }}
                  />
                </FormGroup>

                <FormGroup label={messages.commonEmailReminders}>
                  <Checkbox
                    field="completionExpirationReminders"
                    id="completionExpirationReminders"
                    label={messages.commonSendEmailReminders}
                  />
                  <Checkbox
                    field="completionExpirationRemindersOthers"
                    id="completionExpirationRemindersOthers"
                    label={`${messages.commonSendAdditionalEmailReminders}:`}
                    style={{ marginTop: 5 }}
                  />
                  <Emails
                    disabled={
                      !formState.values.completionExpirationRemindersOthers
                    }
                    field="completionExpirationRemindersEmailList"
                    id="completionExpirationRemindersEmailList"
                    style={{ marginLeft: 20, marginTop: 8 }}
                  />
                </FormGroup>

                <FormGroup
                  label={messages.pathsSettingsDefaultRemindersLanguage}
                  labelHelpText={
                    messages.pathsSettingsDefaultRemindersLanguageTooltip
                  }
                >
                  <Select
                    disabled={
                      !formState.values.completionExpirationReminders &&
                      !formState.values.completionExpirationRemindersOthers
                    }
                    field="defaultRemindersLanguage"
                    id="defaultRemindersLanguage"
                    options={Object.keys(localeNames).map((localeKey) => ({
                      label: localeNames[localeKey],
                      value: localeKey
                    }))}
                    style={{ maxWidth: 200 }}
                  />
                </FormGroup>

                <ReminderIntervalsSelector
                  disabled={
                    !formState.values.completionExpirationReminders &&
                    !formState.values.completionExpirationRemindersOthers
                  }
                  field="completionExpirationReminderIntervals"
                  type="completion"
                />
              </FormSection>
            )}

            <FormSection>
              {(formState.values.automaticExpiration &&
                formState.values.monthsBeforeExpiration !==
                  achievement.monthsBeforeExpiration) ||
              (!formState.values.automaticExpiration &&
                achievement.monthsBeforeExpiration !== null) ? (
                <ConfirmButton
                  actionText={messages.commonSaveChanges}
                  buttonText={messages.commonSaveChanges}
                  category="success"
                  confirmText={messages.achievementExpirationChanged}
                  confirmTitle={messages.commonSaveChanges}
                  onConfirm={handleConfirm}
                />
              ) : (
                <Button
                  category="success"
                  disabled={formState.invalid}
                  loading={loading}
                  text={messages.commonSaveChanges}
                  type="submit"
                />
              )}
            </FormSection>
          </>
        )}
      </Form>
    </Box>
  );
};

export default AchievementSettingsCompletion;
